/**
 * Contractregime per datum — welke dienstverbandvorm gold wanneer.
 *
 * De loonkaart (`staff`) is een momentopname: één contract_type, één
 * uren_per_week. Jaarberekeningen (vakantietegoed, 10%-opbouw per loonperiode)
 * hebben de vorm per dátum nodig, anders rekent een wissel midden in het jaar
 * (Skyler Venema min-max 20u → jaaruren 38u per 20-09-2026) het hele jaar op
 * de nieuwe vorm. De contracthistorie (`contracts`, incl. beëindigd) is de
 * regimehistorie; de kaart is de terugval.
 *
 * Regels:
 * - Een contract geldt vanaf start_date t/m end_date, of t/m de dag vóór het
 *   volgende contract als dat eerder begint. Alleen contracten die in werking
 *   zijn (geweest): getekend, beëindigd, verlopen.
 * - Zonder enig contract: de kaart geldt het hele dienstvenster (oude gedrag).
 * - Met contracten: gaten (vóór het eerste contract) hebben géén vaste basis;
 *   voor de 10%-opbouw per periode geldt in een gat de kaartvorm. Wie vóór zijn
 *   eerste contractrij al een vaste vorm had, krijgt een archiefrij
 *   (status beëindigd) — zie heel-contractwissel-2026-10.ts.
 * - Vakantiebasis per vast segment: cao 26 dagen × uren/5, naar rato dagen in
 *   het jaar (kaart-vakantiedagen overrulen 26); vast-salaris gebruikt
 *   contract vakantieuren_per_jaar. 0-uren/student/zzp/dga: geen basis.
 * - 10%-opbouw per loonperiode, naar het regime aan het periode-einde:
 *   0-uren/student over betaalde uren; vast/min-max over meerwerk boven de
 *   maandnorm (uren × 52/12); jaaruren/vast-salaris niets (jaaruren: pas bij
 *   uitbetaling van spaaruren, zie urensaldo-calculator).
 * - 0-uren all-in: vakantie zit in het uurloon, géén opbouw; 0-uren-opbouw:
 *   10% over betaalde uren. Zonder contractrij beslist `staff.loon_type`
 *   ('all-in' → geen opbouw). Vóór 03-11-2026 werden beide varianten dezelfde
 *   vorm en bouwde Joost (all-in t/m 19-10) 134,6u fantoomtegoed op dat bij
 *   zijn wissel naar jaaruren zichtbaar werd. De NMBRS-export rekent bij
 *   all-in wél 10%: dat is daar de uitbetaling in geld, bewust.
 * - Lagen (vakantieBasisPerLaag): wettelijk = 4 × weekuren per jaar (cao 20
 *   dagen), vast-salaris min(vakantieuren_per_jaar, 4 × uren); bovenwettelijk
 *   = de rest. Verval en FIFO zitten in vakantie-saldo.ts.
 *
 * ═══ BRON VAN WAARHEID ═══
 * Byte-identieke spiegels (Vercel-functions importeren niet buiten hun
 * app-root, Vite-apps geen workspace-deps). Wijzig je iets, wijzig dan ALLE
 * DRIE en draai scripts/smoke-workforce.sh (hash-pariteitscheck):
 *   1. brands/cinco/apps/office-app/src/lib/contract-regime.ts   (canoniek)
 *   2. apps/team-app/api/_lib/contract-regime.ts
 *   3. brands/cinco/apps/office-app/api/_lib/contract-regime.ts  (vakantie-saldo-loader)
 */

export type KaartVorm = 'vast' | 'min-max' | '0-uren' | 'student' | 'zzp' | 'dga' | 'jaaruren' | 'vast-salaris';

export interface RegimeContract {
  contract_number?: string | null;
  employment_type: string;
  status: string;
  start_date: string;
  end_date: string | null;
  uren_per_week: number | null;
  uren_min?: number | null;
  vakantieuren_per_jaar?: number | null;
}

export interface RegimeKaart {
  contract_type: string | null;
  uren_per_week: number | null;
  /** 'all-in' = vakantie in het uurloon (loonkaart-sync zet dit uit het contract); terugval zonder contractrij */
  loon_type?: string | null;
  datum_in_dienst?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  vakantiedagen_wettelijk?: number | null;
  vakantiedagen_bovenwettelijk?: number | null;
}

export interface RegimeSegment {
  from: string; // YYYY-MM-DD, binnen het jaar
  to: string;   // YYYY-MM-DD, binnen het jaar
  vorm: KaartVorm;
  urenPerWeek: number | null;
  vakantieurenPerJaar: number | null; // alleen vast-salaris (contractwaarde)
  bron: 'contract' | 'kaart';
  contractNummer: string | null;
  /** true = all-in: vakantie zit in het uurloon, geen 10%-opbouw */
  vakantieInUurloon: boolean;
}

export interface Regime {
  vorm: KaartVorm;
  urenPerWeek: number | null;
  bron: 'contract' | 'kaart';
  vakantieInUurloon: boolean;
}

const IN_WERKING = new Set(['getekend', 'beëindigd', 'verlopen']);
export const VASTE_BASIS = new Set<KaartVorm>(['vast', 'min-max', 'jaaruren', 'vast-salaris']);
export const CAO_VAKANTIEDAGEN = 26;
/** Wettelijk minimum: vier maal de weekuren (cao: 20 van de 26 dagen). */
export const WETTELIJKE_VAKANTIEDAGEN = 20;

/** Vakantie in het uurloon? Uit het contracttype, anders uit de kaart. */
export function vakantieInUurloonUitContract(employmentType: string): boolean {
  return employmentType === '0-uren-allin';
}

function kaartVakantieInUurloon(kaart: RegimeKaart): boolean {
  return kaart.loon_type === 'all-in';
}

/** contracts.employment_type → vorm op de kaart (zelfde tabel als loonkaart-sync). */
export function vormUitContract(employmentType: string): KaartVorm | null {
  switch (employmentType) {
    case 'regulier': return 'vast';
    case 'jaaruren': return 'jaaruren';
    case 'min-max': return 'min-max';
    case 'vast-salaris': return 'vast-salaris';
    case '0-uren-allin':
    case '0-uren-opbouw': return '0-uren';
    default: return null;
  }
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function dagVoor(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() - 1);
  return fmt(d);
}

function fmt(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function dagenInclusief(from: string, to: string): number {
  const a = new Date(from + 'T00:00:00').getTime();
  const b = new Date(to + 'T00:00:00').getTime();
  return Math.round((b - a) / 86400000) + 1;
}

export function jaarDagen(year: number): number {
  return dagenInclusief(`${year}-01-01`, `${year}-12-31`);
}

function kaartVorm(kaart: RegimeKaart): KaartVorm {
  return (kaart.contract_type as KaartVorm) || '0-uren';
}

/**
 * Regimesegmenten binnen één kalenderjaar, chronologisch, zonder overlap.
 */
export function regimeSegments(year: number, contracts: RegimeContract[], kaart: RegimeKaart): RegimeSegment[] {
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  // Dienstvenster van de kaart (uit dienst klemt alles af)
  const dienstStart = kaart.datum_in_dienst || kaart.start_date || null;
  const vensterFrom = dienstStart && dienstStart > yearStart ? dienstStart : yearStart;
  const vensterTo = kaart.end_date && kaart.end_date < yearEnd ? kaart.end_date : yearEnd;
  if (vensterFrom > vensterTo) return [];

  const inWerking = contracts
    .filter(c => IN_WERKING.has(c.status) && vormUitContract(c.employment_type))
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  if (inWerking.length === 0) {
    return [{
      from: vensterFrom, to: vensterTo,
      vorm: kaartVorm(kaart), urenPerWeek: kaart.uren_per_week ?? null,
      vakantieurenPerJaar: null, bron: 'kaart', contractNummer: null,
      vakantieInUurloon: kaartVakantieInUurloon(kaart),
    }];
  }

  const segments: RegimeSegment[] = [];
  for (let i = 0; i < inWerking.length; i++) {
    const c = inWerking[i]!;
    const volgende = inWerking[i + 1];
    let to = c.end_date ?? yearEnd;
    if (volgende && volgende.start_date <= to) to = dagVoor(volgende.start_date);
    const from = c.start_date > vensterFrom ? c.start_date : vensterFrom;
    const clampedTo = to < vensterTo ? to : vensterTo;
    if (from > clampedTo) continue;
    const vorm = vormUitContract(c.employment_type)!;
    const uren = c.employment_type === 'min-max' ? (c.uren_min ?? c.uren_per_week) : c.uren_per_week;
    segments.push({
      from, to: clampedTo, vorm,
      urenPerWeek: uren != null ? Number(uren) : null,
      vakantieurenPerJaar: vorm === 'vast-salaris' && c.vakantieuren_per_jaar != null ? Number(c.vakantieuren_per_jaar) : null,
      bron: 'contract', contractNummer: c.contract_number ?? null,
      vakantieInUurloon: vakantieInUurloonUitContract(c.employment_type),
    });
  }
  return segments;
}

/** Regime op één datum: segment, anders de kaart (gat of vóór de historie). */
export function regimeOp(dateISO: string, segments: RegimeSegment[], kaart: RegimeKaart): Regime {
  const s = segments.find(seg => seg.from <= dateISO && dateISO <= seg.to);
  if (s) return { vorm: s.vorm, urenPerWeek: s.urenPerWeek, bron: s.bron, vakantieInUurloon: s.vakantieInUurloon };
  return { vorm: kaartVorm(kaart), urenPerWeek: kaart.uren_per_week ?? null, bron: 'kaart', vakantieInUurloon: kaartVakantieInUurloon(kaart) };
}

export interface VakantieBasisLagen {
  /** 4 × weekuren naar rato (cao 20 dagen); vast-salaris min(contractwaarde, 4 × uren) */
  wettelijk: number;
  /** basis min wettelijk (cao 6 dagen; vast-salaris de rest van vakantieuren_per_jaar) */
  bovenwettelijk: number;
  /** = vakantieBasisVoorJaar (som vóór afronding) */
  totaal: number;
}

function basisRaw(year: number, segments: RegimeSegment[], kaart: RegimeKaart): { wettelijk: number; totaal: number } {
  const dagen = jaarDagen(year);
  const kaartDagen = (kaart.vakantiedagen_wettelijk ?? WETTELIJKE_VAKANTIEDAGEN) + (kaart.vakantiedagen_bovenwettelijk ?? 6);
  const vakantieDagen = kaartDagen > 0 ? kaartDagen : CAO_VAKANTIEDAGEN;
  const wettelijkDagen = kaart.vakantiedagen_wettelijk ?? WETTELIJKE_VAKANTIEDAGEN;
  let totaal = 0;
  let wettelijk = 0;
  for (const s of segments) {
    if (!VASTE_BASIS.has(s.vorm)) continue;
    const uren = s.urenPerWeek ?? 38;
    const perJaar = s.vorm === 'vast-salaris' && s.vakantieurenPerJaar != null
      ? s.vakantieurenPerJaar
      : vakantieDagen * (uren / 5);
    const wettelijkPerJaar = Math.min(perJaar, wettelijkDagen * (uren / 5));
    const rato = dagenInclusief(s.from, s.to) / dagen;
    totaal += perJaar * rato;
    wettelijk += wettelijkPerJaar * rato;
  }
  return { wettelijk, totaal };
}

/**
 * Vaste vakantiebasis voor het jaar: som van de vaste segmenten naar rato.
 * 0-uren/student/zzp/dga-segmenten dragen niets bij (10% loopt per periode).
 */
export function vakantieBasisVoorJaar(year: number, segments: RegimeSegment[], kaart: RegimeKaart): number {
  return round2(basisRaw(year, segments, kaart).totaal);
}

/**
 * Dezelfde basis in twee lagen. Wettelijk = 4 × weekuren per jaar (art. 7:634
 * BW), vast-salaris min(vakantieuren_per_jaar, 4 × uren); bovenwettelijk = de
 * rest. Som van de twee afgeronde lagen kan 0,01 afwijken van `totaal`.
 */
export function vakantieBasisPerLaag(year: number, segments: RegimeSegment[], kaart: RegimeKaart): VakantieBasisLagen {
  const { wettelijk, totaal } = basisRaw(year, segments, kaart);
  return { wettelijk: round2(wettelijk), bovenwettelijk: round2(Math.max(0, totaal - wettelijk)), totaal: round2(totaal) };
}

/**
 * 10%-vakantieopbouw voor één loonperiode, naar het regime op het periode-einde.
 * `paidHours` = uitbetaalde uren (0-uren/student), `workedHours` = gewerkt (meerwerk vast/min-max).
 */
export function vakantieExtraVoorPeriode(regime: Regime, workedHours: number, paidHours: number): number {
  if (regime.vakantieInUurloon) return 0;
  switch (regime.vorm) {
    case '0-uren':
    case 'student':
      return round2(paidHours * 0.10);
    case 'vast':
    case 'min-max': {
      const maandNorm = (regime.urenPerWeek ?? 0) * 52 / 12;
      return round2(Math.max(0, workedHours - maandNorm) * 0.10);
    }
    default:
      return 0;
  }
}
